import * as React from 'react';

type PossibleRef<T> = React.Ref<T> | undefined;

type RefCacheNode = WeakMap<object, RefCacheNode | React.RefCallback<unknown>>;

const cacheTerminalKey = {};
const undefinedRefKey = {};
const composedRefCache: RefCacheNode = new WeakMap();

function setRef<T>(ref: PossibleRef<T>, value: T | null) {
  if (typeof ref === 'function') {
    return ref(value);
  }

  if (ref) {
    ref.current = value;
  }

  return undefined;
}

function refCacheKey<T>(ref: PossibleRef<T>): object {
  return (ref ?? undefinedRefKey) as object;
}

function createComposedRefCallback<T>(refs: PossibleRef<T>[]): React.RefCallback<T> {
  let lastNode: T | null | typeof undefinedRefKey = undefinedRefKey;
  let lastCleanup: ReturnType<React.RefCallback<T>> = undefined;

  return (node) => {
    if (lastNode === node) return lastCleanup;
    lastNode = node;

    let hasCleanup = false;
    const cleanups = refs.map((ref) => {
      const cleanup = setRef(ref, node);
      if (!hasCleanup && typeof cleanup === 'function') {
        hasCleanup = true;
      }
      return cleanup;
    });

    lastCleanup = hasCleanup ? () => {
      for (let index = 0; index < cleanups.length; index += 1) {
        const cleanup = cleanups[index];
        if (typeof cleanup === 'function') {
          cleanup();
        } else {
          setRef(refs[index], null);
        }
      }
    } : undefined;

    return lastCleanup;
  };
}

export function composeRefs<T>(...refs: PossibleRef<T>[]): React.RefCallback<T> {
  let cacheNode = composedRefCache;

  for (const ref of refs) {
    const key = refCacheKey(ref);
    const cached = cacheNode.get(key);
    if (cached instanceof WeakMap) {
      cacheNode = cached;
      continue;
    }

    const nextNode: RefCacheNode = new WeakMap();
    cacheNode.set(key, nextNode);
    cacheNode = nextNode;
  }

  const cachedCallback = cacheNode.get(cacheTerminalKey);
  if (typeof cachedCallback === 'function') {
    return cachedCallback as React.RefCallback<T>;
  }

  const callback = createComposedRefCallback(refs);
  cacheNode.set(cacheTerminalKey, callback as React.RefCallback<unknown>);
  return callback;
}

export function useComposedRefs<T>(...refs: PossibleRef<T>[]): React.RefCallback<T> {
  const refsRef = React.useRef(refs);
  const lastNodeRef = React.useRef<T | null | typeof undefinedRefKey>(undefinedRefKey);
  const lastCleanupRef = React.useRef<ReturnType<React.RefCallback<T>>>(undefined);

  React.useLayoutEffect(() => {
    refsRef.current = refs;
  });

  // Keep the callback identity stable when Radix passes inline state-setting refs.
  // React 19 otherwise detaches/reattaches the ref on every render and can recurse.
  return React.useCallback((node) => {
    if (lastNodeRef.current === node) return lastCleanupRef.current;
    lastNodeRef.current = node;

    let hasCleanup = false;
    const currentRefs = refsRef.current;
    const cleanups = currentRefs.map((ref) => {
      const cleanup = setRef(ref, node);
      if (!hasCleanup && typeof cleanup === 'function') {
        hasCleanup = true;
      }
      return cleanup;
    });

    lastCleanupRef.current = hasCleanup ? () => {
      for (let index = 0; index < cleanups.length; index += 1) {
        const cleanup = cleanups[index];
        if (typeof cleanup === 'function') {
          cleanup();
        } else {
          setRef(currentRefs[index], null);
        }
      }
    } : undefined;

    return lastCleanupRef.current;
  }, []);
}
